"use client"

import { Link } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"

import { RobinAvatar } from "@/components/agentic-ats/robin/robin-avatar"
import { stageHex } from "@/lib/stage-colors"
import type { DeskGroup, DeskJob, Trouble } from "@/lib/desk"
import type { MorningDesk } from "@/hooks/use-morning-desk"
import { cn } from "@/lib/utils"

/**
 * The dashboard, rebuilt around where a recruiter's attention should go.
 *
 * Top to bottom: the claim, the receipts it is derived from, the queue, and
 * today's commitments. The queue is the page — everything above it is about a
 * fifth of the pixels — because the claim and the figures exist to justify it,
 * not the other way round.
 *
 * The groups are the diagnosis. A single severity-ranked list would make the
 * reader work out what *kind* of problem each row is on every row, and the
 * response differs completely: a starving job needs hours of sourcing, a stuck
 * one needs a two-minute call. So the heading carries the verb and the rows
 * inherit it.
 *
 * Presentational only; `useMorningDesk` decides what any of this says.
 */

const GROUP_LABEL: Record<Trouble, { name: string; verb: string; why: string }> = {
  starving: {
    name: "Nobody in these",
    verb: "source",
    why: "days open against what has reached a submit",
  },
  stuck: {
    name: "Stuck",
    verb: "chase",
    why: "quiet longest first",
  },
  leaking: {
    name: "Leaking",
    verb: "rework the brief",
    why: "rejections concentrated at one stage",
  },
}

/**
 * The row's evidence. On a starving job it is mostly empty track, so the
 * problem reads before the words do.
 */
function Shape({ row }: { row: DeskJob }) {
  const total = row.shape.reduce((sum, stage) => sum + stage.count, 0)

  return (
    <span className="flex flex-col gap-[5px]">
      <span className="flex h-[5px] gap-[2px] overflow-hidden rounded-full bg-foreground/[0.07]">
        {total > 0 &&
          row.shape
            .filter((stage) => stage.count > 0)
            .map((stage) => (
              <span
                key={stage.stage}
                className="block h-full rounded-full"
                style={{
                  width: `${(stage.count / total) * 100}%`,
                  backgroundColor: stageHex(stage.stage),
                }}
              />
            ))}
      </span>
      <span className="whitespace-nowrap font-mono text-[10px] leading-none text-muted-foreground">
        {total === 0 ? "empty" : `${row.live} in play`}
      </span>
    </span>
  )
}

function Row({ row }: { row: DeskJob }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-3 @md:grid-cols-[minmax(0,1fr)_96px_auto] @2xl:grid-cols-[minmax(0,1fr)_124px_auto] @2xl:gap-4 @2xl:px-4">
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-medium leading-[1.25]">
          {row.title}
          {row.company && (
            <span className="font-normal text-muted-foreground"> · {row.company}</span>
          )}
        </span>
        <span className="mt-1 block truncate font-mono text-[10.5px] leading-none text-muted-foreground">
          {row.why}
        </span>
      </span>

      {/*
        The bar is the row's evidence, so it is gated at @md rather than @2xl:
        the ATS pane sits around 460–560px most of the time, and @2xl is 672,
        which would have hidden the thing that makes an empty pipeline readable
        at exactly the widths the app actually runs at.
      */}
      <span className="hidden @md:block">
        <Shape row={row} />
      </span>

      <Link
        to="/jobs/$jobId/overview"
        params={{ jobId: row.id }}
        className="flex-none whitespace-nowrap rounded-md border border-primary/35 bg-primary/[0.07] px-[11px] py-[7px] text-[12px] font-medium text-brand-ink transition-colors hover:bg-primary/[0.12]"
      >
        {row.action}
      </Link>
    </div>
  )
}

function Group({ group }: { group: DeskGroup }) {
  const label = GROUP_LABEL[group.trouble]

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">
          {label.name} <span className="font-medium text-brand-ink">— {label.verb}</span>
        </span>
        {/* How it ranked, in the dimmest ink on the page. */}
        <span className="ml-auto hidden text-right text-[11px] text-muted-foreground/70 @2xl:block">
          {label.why}
        </span>
      </div>
      <div className="divide-y overflow-hidden rounded-[10px] border bg-card">
        {group.rows.slice(0, 3).map((row) => (
          <Row key={row.id} row={row} />
        ))}
      </div>
      {group.rows.length > 3 && (
        <p className="text-[11.5px] text-muted-foreground">
          {group.rows.length - 3} more like this.
        </p>
      )}
    </div>
  )
}

export function MorningDeskPage({ desk }: { desk: MorningDesk }) {
  const { summary } = desk

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <header className="shrink-0 border-b bg-chrome px-4 pb-3 pt-4 @2xl:px-6">
        <h1 className="text-[15px] font-medium leading-tight tracking-[-0.01em] @2xl:text-2xl @2xl:font-semibold @2xl:tracking-[-0.025em]">
          Dashboard
        </h1>
      </header>

      {/* The claim — prose, from the figures directly beneath it and nothing else. */}
      <div className="flex shrink-0 items-start gap-2.5 border-b bg-chrome px-4 pb-3.5 pt-3 @2xl:px-6">
        <RobinAvatar state="idle" className="mt-px h-[18px] w-[18px] flex-none" />
        {desk.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Reading the desk…
          </p>
        ) : (
          <p className="text-pretty text-xs leading-[1.5] @2xl:text-[12.5px]">
            {desk.isError
              ? "The desk cannot be read right now, so there is nothing here worth trusting."
              : desk.claim}
          </p>
        )}
      </div>

      {/*
        The receipts: one line, so the weight stays on the queue.

        Suppressed while it is still reading, not only on error — `summary` is
        derived from an empty fallback until job-risks lands, so this printed
        "0 open · 0 starving" underneath the words "Reading the desk…".
      */}
      {!desk.isError && !desk.isLoading && (
        <div className="flex shrink-0 flex-wrap items-baseline gap-x-4 gap-y-1 border-b bg-chrome px-4 py-2 font-mono text-[11px] tabular-nums text-muted-foreground @2xl:px-6">
          <span>
            <b className="font-medium text-foreground">{summary.open}</b> open
          </span>
          <span className={summary.starving > 0 ? "text-destructive" : undefined}>
            <b className="font-medium">{summary.starving}</b> starving
          </span>
          <span className={summary.stuck > 0 ? "text-attention" : undefined}>
            <b className="font-medium">{summary.stuck}</b> stuck
          </span>
          <span>
            <b className="font-medium text-foreground">{summary.leaking}</b> leaking
          </span>
          <span>
            <b className="font-medium text-foreground">{summary.healthy}</b> healthy
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-3.5 @2xl:px-6">
        <div className="flex flex-col gap-5">
          {desk.groups.map((group) => (
            <Group key={group.trouble} group={group} />
          ))}

          {!desk.isLoading && !desk.isError && desk.groups.length === 0 && (
            <p className="rounded-[10px] border bg-card px-4 py-5 text-[12.5px] text-muted-foreground">
              Nothing is stuck and nothing is starving. A blank page on a good day
              teaches people to stop opening it, so: {summary.open} open{" "}
              {summary.open === 1 ? "job" : "jobs"}, all moving.
            </p>
          )}

          {/*
            Due tasks make this section worth showing on their own. It used to
            hang off the appointment list, so a day with three overdue tasks and
            no meetings silently dropped them.
          */}
          {(desk.today.length > 0 || desk.routineCount > 0) && (
            <div className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">
                Today
              </span>
              <div className="divide-y overflow-hidden rounded-[10px] border bg-card">
                {desk.today.map((item) => (
                  <div key={item.id} className="flex items-baseline gap-3.5 px-3.5 py-2.5">
                    <span
                      className={cn(
                        "w-[42px] flex-none font-mono text-[11px] tabular-nums",
                        item.soon ? "text-brand-ink" : "text-muted-foreground",
                      )}
                    >
                      {item.at}
                    </span>
                    <span className="min-w-0 truncate text-[12.5px]">
                      {item.title}
                      {item.detail && (
                        <span className="text-muted-foreground"> · {item.detail}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {desk.routineCount > 0 && (
                <p className="font-mono text-[10.5px] text-muted-foreground">
                  {desk.routineCount} {desk.routineCount === 1 ? "task" : "tasks"} due
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
